import { DateTime } from "luxon";
import {
  Box,
  CircularProgress,
  Divider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { packageColumns } from "../../permitComponents/permitPackageTabs/permitTypeColumns";
import { getPermitPackages } from "../../../store/actions/dataActions";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  generateExternalSummary,
  generateInternalSummary,
} from "./customerPermits/generateSummaries";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { setActiveTab } from "../../../store/actions/uiActions/myPermitsActions";
import { formatCity } from "../../permitComponents/permitComponentHelper";
import PackageSettingsModal from "./managementModals/PackageSettingsModal";
import PackagePaymentDetails from "./components/PackagePaymentDetails";
import PackageNotificationSwitch from "./components/PackageNotificationSwitch";

const RenderCustomerPermits = ({
  packagePage,
  setPackagePage,
  packagePageSize,
  setpackagePageSize,
  packageLoading,
  customerSelectedRow,
  packageRowCount,
  setPackageRowCount,
  selectedPackage,
  setSelectedPackage,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const permitPackages = useSelector((state) => state.nocData?.permitPackages);
  const { activeTab } = useSelector((state) => state.myPermitsComponent);

  const dispatch = useDispatch();
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [dataLoading, setDataLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [documentDonwloadLoading, setDocumentDownloadLoading] = useState(false);
  const [sendDocOwnerLoading, setSendDocOwnerLoading] = useState(false);
  const [sendDocContractorLoading, setSendDocContractorLoading] =
    useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [emailSentMessage, setEmailSentMessage] = useState("");
  const [emailSentError, setEmailSentError] = useState("");
  const [packageSettingsModalOpen, setPackageSettingsModalOpen] =
    useState(false);

  const didInit = useRef(false);
  const selectedPackageRef = useRef(null);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      if (activeTab !== 0) {
        dispatch(setActiveTab(0));
      }
    }
  }, [activeTab, dispatch]);

  const compactStatusChip = (label, value, allowNA = false) => {
    const isNA = allowNA && value === null;

    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}>
          {label}
        </Typography>
        {isNA ? (
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}>
            N/A
          </Typography>
        ) : value ? (
          <CheckCircleIcon sx={{ color: green[500], fontSize: "1rem" }} />
        ) : (
          <CancelIcon sx={{ color: red[500], fontSize: "1rem" }} />
        )}
      </Box>
    );
  };

  const mobileGridColumns = useMemo(
    () => [
      {
        field: "mobile",
        headerName: "Packages",
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const isCustomDoc = Boolean(row?.cst_packageID);

          const packageName = isCustomDoc
            ? "Custom Documents"
            : row?.package_name || "N/A";

          const projectAddress = isCustomDoc
            ? row?.document_location?.project_address || "N/A"
            : row?.project_address || "N/A";

          const county = isCustomDoc
            ? row?.document_location?.county || "N/A"
            : row?.county || "N/A";

          const city = isCustomDoc ? "N/A" : formatCity(row?.city);

          // Dates
          let initiatedDate = "N/A";
          if (isCustomDoc) {
            const notarize = params?.row?.document?.actions?.notarize;
            const record = params?.row?.document?.actions?.record;

            let utcISO = null;

            if ((notarize && record) || notarize) {
              utcISO = params?.row?.processing_permit?.notarization?.started_at;
            } else if (record) {
              utcISO = params?.row?.processing_permit?.recording?.started_at;
            }

            if (utcISO) {
              const formatted = DateTime.fromISO(utcISO, { zone: "utc" })
                .setZone("America/New_York")
                .toFormat("MMM dd");

              initiatedDate = formatted;
            }
            initiatedDate = "N/A";
          } else {
            const timestamp = params?.row?.permit_package_added_at * 1000; // Convert seconds to milliseconds
            const timezone = "America/New_York"; // Florida timezone
            const zonedDate = DateTime.fromMillis(timestamp, {
              zone: "utc",
            }).setZone(timezone);
            initiatedDate = zonedDate.toFormat("MMM dd");
          }

          // Status flags (match your existing logic)
          const nocCreated = isCustomDoc ? null : Boolean(row?.noc_created);

          const hasMultipleDocs = (row?.selected_documents?.length || 0) > 1;
          const ppCreated = isCustomDoc
            ? null
            : Boolean(row?.permit_package_created && hasMultipleDocs);

          const isNotarized =
            row?.notarized ||
            (nocCreated && row?.noc_notarized === true) ||
            (ppCreated && row?.pp_notarized === true);

          const notarized = isCustomDoc
            ? row?.document?.actions?.notarize
              ? Boolean(isNotarized)
              : null
            : Boolean(isNotarized);

          const recorded = isCustomDoc
            ? row?.document?.actions?.record
              ? Boolean(
                  row?.processing_permit?.recording?.recorded || row?.recorded,
                )
              : null
            : Boolean(row?.recorded);

          return (
            <Box
              sx={{
                width: "100%",
                py: 0.6,
                px: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.45,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                    }}
                  >
                    {packageName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "rgba(0,0,0,0.62)",
                      lineHeight: 1.2,
                      mt: 0.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {params?.row?.email?.split("@")[0] || ""}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "rgba(0,0,0,0.62)",
                      lineHeight: 1.2,
                      mt: 0.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {projectAddress}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.74rem",
                      color: "rgba(0,0,0,0.62)",
                      lineHeight: 1.2,
                      mt: 0.15,
                    }}
                  >
                    {county} • {city}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "rgba(0,0,0,0.62)",
                    flexShrink: 0,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {initiatedDate}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.0 }}>
                {compactStatusChip("NOC Created", nocCreated, true)}
                {compactStatusChip("Package Built", ppCreated, true)}
                {compactStatusChip("Notarized", notarized, true)}
                {compactStatusChip("Recorded", recorded, true)}
              </Box>
            </Box>
          );
        },
      },
    ],

    [],
  );

  const handleDownloadCustomerDocs = async () => {
    try {
      setDocumentDownloadLoading(true);
      setDataLoading(true);

      let downloadPDF = "all";
      let pdfName = "";

      if (selectedPackage?.cst_packageID) {
        if (selectedPackage?.contractor?.ORGANIZATION?.nameUnparsed) {
          pdfName = selectedPackage?.contractor?.ORGANIZATION?.nameUnparsed;
        } else if (selectedPackage?.contractor?.PERSON?.business_name) {
          pdfName = selectedPackage?.contractor?.PERSON?.business_name;
        } else if (selectedPackage?.contractor?.PERSON?.first_name) {
          pdfName =
            selectedPackage?.contractor?.PERSON?.first_name +
            " " +
            selectedPackage?.contractor?.PERSON?.last_name;
        } else {
          pdfName = "customer_uploaded_noc";
        }
      } else {
        pdfName = selectedPackage?.contractor?.businessName;
      }

      // Perform the download request
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/admin/download-customer-documents`,
        {
          pdf: downloadPDF, // "all" or specific document
          currentSession: currentSession,
          type: "all", // Ensure type is passed correctly
          task_id: selectedPackage?.task_id,
          cst_packageID: selectedPackage?.cst_packageID,
          customer: {
            email: customerSelectedRow?.email,
            role: customerSelectedRow?.role,
          },
        },
        {
          withCredentials: true,
          responseType: "blob", // Expect binary data in response
        },
      );

      const contentType = response.headers["content-type"];

      // Check if the response is actually an error message (JSON) instead of a blob
      if (contentType && contentType.includes("application/json")) {
        // Convert blob to text to read the JSON error message
        const text = await new Response(response.data).text();
        const errorData = JSON.parse(text);

        if (errorData.success === false) {
          setDownloadError(errorData.message || "Document not ready yet");
          setTimeout(() => {
            setDownloadError("");
          }, 3000);
          return;
        }
      }

      // Determine file extension based on content type
      let fileExtension = "pdf"; // Default to PDF
      if (contentType === "image/png") {
        fileExtension = "png";
      } else if (contentType === "image/jpeg") {
        fileExtension = "jpg";
      } else if (contentType === "application/zip") {
        fileExtension = "zip";
      }

      const fileName = `${pdfName}.${fileExtension}`;

      // Create a URL for the binary response (PDF or ZIP)
      const fileURL = window.URL.createObjectURL(
        new Blob([response.data], { type: contentType }),
      );
      // Create an anchor element to trigger the download
      const fileLink = document.createElement("a");
      fileLink.href = fileURL;
      fileLink.setAttribute("download", fileName); // Set the correct file name (PDF or ZIP)
      document.body.appendChild(fileLink);
      fileLink.click();

      // Clean up after download
      document.body.removeChild(fileLink);
      window.URL.revokeObjectURL(fileURL);
    } catch (e) {
      setDownloadError(e?.response?.data?.message || "Document not ready yet");
      setTimeout(() => {
        setDownloadError("");
      }, 3000);
    } finally {
      setDocumentDownloadLoading(false);
      setDataLoading(false);
    }
  };

  const handleSendDocument = async (type) => {
    try {
      setDataLoading(true);
      if (type === "owner") {
        setSendDocOwnerLoading(true);
      } else if (type === "contractor") {
        setSendDocContractorLoading(true);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/email/send-documents`,
        {
          sendTo: type,
          task_id: selectedPackage?.task_id,
          cst_packageID: selectedPackage?.cst_packageID,
          customer: {
            email: customerSelectedRow?.email,
            role: customerSelectedRow?.role,
          },
          currentSession: currentSession,
        },
        {
          withCredentials: true,
        },
      );
      if (response?.data?.status === "success") {
        setEmailSentMessage(response?.data?.message);
        setTimeout(() => {
          setEmailSentMessage("");
        }, 3000);
      } else {
        setEmailSentError(response?.data?.message);
        setTimeout(() => {
          setEmailSentError("");
        }, 3000);
      }
    } catch (error) {
      setEmailSentError(error?.response?.data?.message || "An error occurred");
      setTimeout(() => {
        setEmailSentError("");
      }, 3000);
    } finally {
      setSendDocOwnerLoading(false);
      setSendDocContractorLoading(false);
      setDataLoading(false);
    }
  };

  const handlePaginationModelChange = async (model, details) => {
    setDataLoading(true);
    setSelectedPackage(null);
    setSummary("");
    const newPage = model.page;
    const newRowsPerPage = model.pageSize;
    setPackagePage(newPage);
    setpackagePageSize(newRowsPerPage);

    const result = await dispatch(
      getPermitPackages({
        newPage: newPage,
        newPageSize: 5,
        customerData: {
          email: customerSelectedRow?.email,
          role: customerSelectedRow?.role,
        },
      }),
    );
    setPackageRowCount(result?.data || 0);
    setpackagePageSize(5);
    setPackagePage(newPage);
    setDataLoading(false);
  };

  const handleRowClick = (params, event, details) => {
    const row = params?.row;
    setSelectedPackage(row);

    let customerSummaryText = "";

    if (row?.cst_packageID) {
      customerSummaryText = generateExternalSummary(row);
    } else {
      customerSummaryText = generateInternalSummary(row);
    }
    setSummary(customerSummaryText);

    setTimeout(
      () =>
        selectedPackageRef.current?.scrollIntoView({
          behavior: "smooth",
        }),
      50,
    );
  };

  const handlePackageNotificationSaved = (updatedPackage) => {
    setSelectedPackage(updatedPackage);
    dispatch({
      type: "GET_PERMIT_PACKAGES",
      payload: (permitPackages || []).map((p) =>
        p?.id === updatedPackage?.id ? updatedPackage : p,
      ),
    });
  };

  return (
    <Box
      className="step13"
      sx={{
        flex: 0.8,
        minHeight: 0,
        overflow: "auto",
      }}
    >
      {packageLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 3,
          }}
        >
          <CircularProgress size={35} />
        </Box>
      ) : !permitPackages || permitPackages?.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 3,
          }}
        >
          <Typography mt={2} align="center" color="primary" variant="h6">
            No Permit Packages Created Yet
          </Typography>
        </Box>
      ) : (
        <Box>
          <DataGrid
            sx={{
              "& .wrapHeader .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "normal",
                lineHeight: 1.2,
                textAlign: "center",
              },
              "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader": {
                "&:hover": {
                  ".MuiDataGrid-menuIcon, .MuiDataGrid-sortIcon, .MuiDataGrid-columnHeader--sorted":
                    {
                      display: "none",
                      pointerEvents: "none",
                    },
                  cursor: "default",
                },
                backgroundColor: "#333333", // Change column header background color
                color: "white", // Optional: Change text color for contrast
              },
              ".MuiDataGrid-menuIcon": {
                display: "none",
              },
              // Additionally target specific sort button
              "& .MuiDataGrid-columnHeader--sortable": {
                pointerEvents: "none",
              },
              "& .MuiDataGrid-columnHeaders": {
                padding: 0, // Adjust as needed
                margin: 0, // Adjust as needed
              },
              "& .MuiDataGrid-columnHeadersInner": {
                width: "100%", // Adjust as needed
                padding: 0,
              },
              border: "1px solid #FCF2F0", // Change the border color of the entire DataGrid
              "& .MuiDataGrid-cell": {
                borderColor: "#333333", // Change cell border color
              },
              "& .MuiDataGrid-columnSeparator": {
                display: "none", // Optional: Hide the column separators
              },

              "& .MuiDataGrid-row": {
                cursor: "pointer", // Change cursor to pointer for rows
                // backgroundColor: "#fff4c0 !important",
              },
              // Apply light yellow background for specified rows
              "& .light-yellow-row": {
                backgroundColor: "#fff4c0",
              },
              "& .selected-row-color": {
                backgroundColor: "#ebf1f4 !important",
              },
              // Apply darker yellow only for selected rows that are light-yellow
              "& .light-yellow-row.Mui-selected": {
                backgroundColor: "#ffe499 !important", // Slightly darker yellow
              },
            }}
            rows={permitPackages || []}
            columns={
              tabletSize
                ? mobileGridColumns
                : packageColumns?.[0]?.filter(
                    (column) => column?.field !== "project_address",
                  )
            }
            getRowId={(row) => row?.id}
            onPaginationModelChange={(model, details) => {
              handlePaginationModelChange(model, details);
            }}
            loading={dataLoading}
            paginationMode="server"
            paginationModel={{
              page: packagePage, // Set the current page value here
              pageSize: packagePageSize, // Set the page size value here
            }}
            rowCount={packageRowCount}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            onRowClick={(params, event, details) => {
              handleRowClick(params, event, details);
            }}
            pageSizeOptions={[5]}
            rowHeight={tabletSize ? 114 : 34}
          />
          {selectedPackage && (
            <>
              <Box p={2} px={mobileSize ? 0 : 2} ref={selectedPackageRef}>
                <Box
                  sx={{
                    mb: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: mobileSize ? "flex-start" : "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h6" color="primary">
                    Permit Package Details
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {selectedPackage?.cst_packageID
                      ? selectedPackage?.document_location?.project_address
                      : selectedPackage?.project_address}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Summary:
                  </Typography>
                  <Typography
                    align={mobileSize ? "left" : "center"}
                    variant="body2"
                  >
                    {summary}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <PackagePaymentDetails
                  customerSelectedRow={customerSelectedRow}
                  selectedPackage={selectedPackage}
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                  Notifications:
                </Typography>
                <PackageNotificationSwitch
                  pkg={selectedPackage}
                  customer={{
                    email: customerSelectedRow?.email,
                    role: customerSelectedRow?.role,
                  }}
                  defaultEnabled={
                    !!customerSelectedRow?.notification_settings?.enabled
                  }
                  onSaved={handlePackageNotificationSaved}
                />
              </Box>

              <Box
                sx={{
                  mb: 3,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <LoadingButton
                  onClick={() => setPackageSettingsModalOpen(true)}
                  size="medium"
                  variant="contained"
                >
                  Package Settings
                </LoadingButton>
              </Box>
            </>
          )}
        </Box>
      )}
      <PackageSettingsModal
        open={packageSettingsModalOpen}
        onClose={() => setPackageSettingsModalOpen(false)}
        selectedPackage={selectedPackage}
        setSelectedPackage={setSelectedPackage}
        customerSelectedRow={customerSelectedRow}
        currentSession={currentSession}
        handleSendDocument={handleSendDocument}
        handleDownloadCustomerDocs={handleDownloadCustomerDocs}
        sendDocOwnerLoading={sendDocOwnerLoading}
        sendDocContractorLoading={sendDocContractorLoading}
        documentDonwloadLoading={documentDonwloadLoading}
        downloadError={downloadError}
        emailSentMessage={emailSentMessage}
        emailSentError={emailSentError}
      />
    </Box>
  );
};

export default RenderCustomerPermits;
